// Split out of src/components/SettingsTab.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only).
// SettingsTab.jsx re-exports everything it exported before, so existing imports keep working.

import { Icon } from "../Common.jsx";
import { CollapsibleSection } from "../ChildView.jsx";
import { OWNER_NAME_MAX, PASSCODE_RE, cleanOwnerName, genPasscode } from "../../lib/constants.js";
import { apiGetPushStatus, apiRequestEmailChange, apiResendVerification, apiRevealJoinToken, apiRevealPasscode, getDeviceId, getDeviceOwnerAdultId, apiIssueJoinTicket, getActiveAdultId, apiInviteAdult, apiInviteAction, apiRemoveAdult, apiRotatePasscode, apiSetAdultPermission, apiGetReferral, apiMintReferral } from "../../lib/api.js";
import { adultLabel, adultsOf, can, isOwner } from "../../lib/adults.js";
import { joinDeepLink, joinQrDataUrl, joinTicketDisplayLink, joinTicketLink } from "../../lib/qrcode.js";
import { browserTimezone, fmtTimestamp, logSetting } from "../../lib/utils.js";
import { t as ct } from "../../lib/content.js";
import { NotificationGroup } from "./Notifications.jsx";
import { shareText } from "../../lib/share.js";
import { markReferralShared, referralShareLink, shareReferral } from "../../lib/referral.js";
import { actor } from "./shared.jsx";
import { SetUpAppCard } from "./Devices.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

/* --------------------------- household management -------------------------- */

// Simple 0–5 nudge, not a hard requirement — the only real rule is PASSCODE_RE (8–30
// printable, no spaces). Rewards length and character variety so a parent gets useful
// feedback while typing, without pretending there's a "correct" passcode shape.
function passcodeStrength(pc) {
  if (!pc) return 0;
  let score = 0;
  if (pc.length >= 8) score++;
  if (pc.length >= 12) score++;
  if (/[a-z]/.test(pc) && /[A-Z]/.test(pc)) score++;
  if (/[0-9]/.test(pc)) score++;
  if (/[^A-Za-z0-9]/.test(pc)) score++;
  return score;
}

function StrengthMeter({ passcode }) {
  const score = passcodeStrength(passcode);
  const { label, colorClass } =
    score <= 1 ? { label: "Weak", colorClass: "bg-rose-400" } :
    score === 2 ? { label: "Fair", colorClass: "bg-amber-400" } :
    score === 3 ? { label: "Good", colorClass: "bg-emerald-400" } :
    { label: "Strong", colorClass: "bg-emerald-500" };
  const filled = Math.min(5, score);
  return (
    <div className="flex items-center gap-2 mb-2">
      <div className="flex gap-1 flex-1">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full ${i < filled ? colorClass : "bg-slate-100"}`} />
        ))}
      </div>
      <span className="text-xs font-medium text-slate-400 flex-shrink-0">{passcode ? label : ""}</span>
    </div>
  );
}

function RuleCheck({ ok, children }) {
  return (
    <div className={`text-xs font-medium flex items-center gap-1.5 ${ok ? "text-emerald-600" : "text-slate-400"}`}>
      <span className="w-3.5 flex-shrink-0 inline-flex items-center">{ok ? <Icon id="ic-check" size={12} style={{ color: "currentColor" }} /> : <span aria-hidden="true">·</span>}</span>
      {children}
    </div>
  );
}

// "Your household code" + "Your email address" (change flow) + "Your passcode"
// (reveal/show/copy + change flow) + "Leave Household" — grouped together as the
// Household category in Settings. The passcode is never present in `state` (GET always
// strips it, same as parentPin) — it's only ever fetched on demand via the PIN-gated
// reveal-passcode call, and only kept in this component's own React state for the
// current session. ownerEmail is NOT a stripped secret the same way — it travels in the
// ordinary state payload like any other setting — so changing it needs no extra
// proof-of-knowledge call beyond already being inside Parent Mode (which itself required
// the PIN to reach). It still gets its own confirm step below simply because it's the
// address passcode recovery emails go to (see worker/handlers/recovery.js) — a
// mis-typed change here quietly breaks that recovery path for next time.
function HouseholdManagementSection({ state, update, showToast, code, verifiedPin, setPendingVerifiedPasscode, setPendingVerifiedJoinToken, onLeaveHousehold, confirmingLeave, setConfirmingLeave, onOpenCategory, onAwaitingJoin }) {
  const [revealedPasscode, setRevealedPasscode] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [showPasscode, setShowPasscode] = useState(false);
  const [changing, setChanging] = useState(false);
  const [newPasscode, setNewPasscode] = useState("");
  const [confirmPasscode, setConfirmPasscode] = useState("");
  const [passcodeError, setPasscodeError] = useState("");
  const [confirmingChange, setConfirmingChange] = useState(false);

  // Owner-email verification status line (review 2.1) — resend re-mints the token and
  // re-sends the verification email, rate-limited server-side (3/code/hour).
  const [resendingVerification, setResendingVerification] = useState(false);
  // LB-370 (Alan, 24 Sep). The row's summary line reads the count WITHOUT minting; opening
  // the row is the "first tap" that mints the household's standing code. Mint-or-get on the
  // server, so reopening never makes a second code.
  const [referral, setReferral] = useState(null);
  const [referralBusy, setReferralBusy] = useState(false);
  const [referralError, setReferralError] = useState("");
  useEffect(() => {
    let alive = true;
    apiGetReferral(code).then((r) => { if (alive && r.ok) setReferral(r); });
    return () => { alive = false; };
  }, [code]);
  async function openReferral() {
    if (referral && referral.minted) return;
    setReferralBusy(true); setReferralError("");
    const r = await apiMintReferral(code);
    setReferralBusy(false);
    if (r.ok) setReferral(r);
    else setReferralError(r.networkError ? ct("settings.referral.err_network", "Couldn't reach Little Bankers. Check your connection and try again.") : (r.error || ct("settings.referral.err_generic", "Couldn't create your link just now")));
  }
  async function shareReferralLink(mode) {
    const out = await shareReferral(code, { mode });
    if (out === "shared" || out === "copied") {
      markReferralShared(update);
      if (out === "copied") showToast(ct("toast.link_copied", "Link copied"), "good");
      const r = await apiGetReferral(code); if (r.ok) setReferral(r);
    } else if (out && out.error === "copy") {
      showToast(ct("toast.copy_failed", "Couldn't copy. Select and copy it manually"), "bad");
    } else if (out && out.error && out.error !== "dismissed") {
      showToast(ct("settings.referral.err_generic", "Couldn't create your link just now"), "bad");
    }
  }
  async function resendVerification() {
    setResendingVerification(true);
    try {
      await apiResendVerification(code);
      showToast(ct("toast.verification_resent", "Confirmation email sent again. Check your inbox"), "good");
    } catch (err) {
      showToast(err.message || ct("toast.verification_resend_failed", "Couldn't resend. Try again shortly"), "bad");
    } finally {
      setResendingVerification(false);
    }
  }

  // Quick-join QR (Aug 2026) — same reveal-on-demand shape as the passcode above, but the
  // token is never typed or shown as text: it only ever exists as the QR image. showQr
  // toggles the reveal call the same way showPasscode does.
  const [joinToken, setJoinToken] = useState(null);
  const [revealingQr, setRevealingQr] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [confirmingRegen, setConfirmingRegen] = useState(false);

  const [changingEmail, setChangingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [confirmingEmailChange, setConfirmingEmailChange] = useState(false);

  // A household still on the "123456" placeholder PIN never went through the PinPad (Parent
  // Mode opened directly, see App.jsx), so `verifiedPin` is null even though the real PIN IS
  // known — it's the default. Mirrors exactly how PinSetupStep proves the initial PIN change.
  const pinToProve = state.settings.pinConfigured ? verifiedPin : "123456";

  async function ensureRevealed() {
    if (revealedPasscode) return revealedPasscode;
    setRevealing(true);
    try {
      const passcode = await apiRevealPasscode(code, pinToProve);
      setRevealedPasscode(passcode);
      return passcode;
    } catch (err) {
      showToast(err.message || ct("toast.passcode_retrieve_failed", "Couldn't retrieve the passcode"), "bad");
      return null;
    } finally {
      setRevealing(false);
    }
  }

  async function toggleShowPasscode() {
    if (!showPasscode) {
      const p = await ensureRevealed();
      if (p === null && !revealedPasscode) return; // reveal failed and we still have nothing to show
    }
    setShowPasscode((v) => !v);
  }

  async function copyPasscode() {
    const p = await ensureRevealed();
    if (!p) { showToast(ct("toast.no_passcode_set", "No passcode set yet for this household"), "bad"); return; }
    try {
      await navigator.clipboard.writeText(p);
      showToast(ct("toast.passcode_copied", "Passcode copied"), "good");
    } catch {
      showToast(ct("toast.copy_failed", "Couldn't copy. Select and copy it manually"), "bad");
    }
  }

  // Quick-join QR (Aug 2026): reveal-on-demand, same PIN gate as the passcode above — a QR
  // never shows a parent anything they couldn't already get to via "Show" on the passcode.
  // A household that's never needed one yet gets a fresh token minted right here, client-
  // side, and saved through the normal update() flow (exactly how a brand-new household's
  // very first passcode is set at creation) rather than a separate server write endpoint.
  async function ensureJoinTokenRevealed() {
    if (joinToken) return joinToken;
    setRevealingQr(true);
    try {
      // LB-396 (security fix run 1, Part 3): the worker mints the first token behind the PIN
      // reveal and returns it; a save can no longer introduce one, so the client stops trying.
      const token = await apiRevealJoinToken(code, pinToProve);
      if (!token) throw new Error(ct("toast.jointoken_retrieve_failed", "Couldn't retrieve the quick-join code"));
      setJoinToken(token);
      return token;
    } catch (err) {
      showToast(err.message || ct("toast.jointoken_retrieve_failed", "Couldn't retrieve the quick-join code"), "bad");
      return null;
    } finally {
      setRevealingQr(false);
    }
  }

  async function toggleShowQr() {
    if (!showQr) {
      const t = await ensureJoinTokenRevealed();
      if (t === null && !joinToken) return; // reveal failed and we still have nothing to show
    }
    setShowQr((v) => !v);
  }

  // Regenerating replaces the token behind the QR without touching the household passcode —
  // the two are deliberately separate secrets (Aug 2026 scoping) precisely so this rotation
  // never forces a parent to also re-memorise/re-share a new manually-typed passcode.
  async function confirmRegenerateQr() {
    const current = await ensureJoinTokenRevealed(); // proves we know the old value
    const fresh = genPasscode(16);
    setPendingVerifiedJoinToken(current);
    update((s) => { s.settings.householdJoinToken = fresh; return s; });
    setJoinToken(fresh);
    setShowQr(true);
    setConfirmingRegen(false);
    showToast(ct("toast.qr_regenerated", "New QR generated — any old QR (or a screenshot of it) stops working"), "good");
  }

  async function openChange() {
    await ensureRevealed(); // may resolve to null if none set yet — the form still opens fine
    setNewPasscode(""); setConfirmPasscode(""); setPasscodeError(""); setConfirmingChange(false);
    setChanging(true);
  }

  // LB-337 — the owner's first name, edited in place beside the email. No confirm loop and
  // no server round trip: a name is not a credential (see the save handler in worker/index.js),
  // so this is an ordinary update() like every other Settings field. Blank saves are a
  // cancel, not an error — clearing your own name back to nothing is a legitimate thing to
  // want, and telling a parent off for it would be principle 5 in reverse.
  // Starts OPEN when there is no name: an empty value with an Add button is a button that
  // exists to let you type. With a name it starts closed, because that is a display of
  // something true with an Edit beside it.
  const [editingName, setEditingName] = useState(() => !state.ownerName);
  const [newName, setNewName] = useState("");
  function openChangeName() {
    setNewName(state.ownerName || "");
    setEditingName(true);
  }
  // Seed the field when the row opens in edit mode on first render (no name yet).
  useEffect(() => { if (!state.ownerName) setNewName(""); }, [state.ownerName]);
  function saveOwnerName() {
    const cleaned = cleanOwnerName(newName);
    update((s) => { s.ownerName = cleaned; return s; });
    setEditingName(false);
    showToast(ct("toast.owner_name_saved", "Name saved"), "good");
  }

  function openChangeEmail() {
    setNewEmail(state.ownerEmail || "");
    setEmailError("");
    setConfirmingEmailChange(false);
    setChangingEmail(true);
  }
  function submitEmailChange() {
    const trimmed = newEmail.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setEmailError("Enter a valid email address."); return; }
    setNewEmail(trimmed);
    setEmailError("");
    setConfirmingEmailChange(true);
  }
  // W2-PR(iii) part 2: the change is no longer a direct client write — that let any
  // PIN-holder silently re-point recovery and deletion at an unproven address. Now it
  // ASKS the server, which emails a confirm link to the NEW address; the change lands
  // only when that link is clicked, and the old address is notified when it does.
  const [emailChangePending, setEmailChangePending] = useState(null); // email string | null
  async function confirmEmailChange() {
    try {
      await apiRequestEmailChange(code, newEmail.trim(), verifiedPin);
      setEmailChangePending(newEmail.trim());
      showToast(ct("toast.email_change_requested", "Confirmation sent — the change happens when that link is clicked"), "good");
    } catch (err) {
      showToast(err.message || ct("toast.email_change_failed", "Couldn't send the confirmation. Try again shortly"), "bad");
    }
    setChangingEmail(false); setConfirmingEmailChange(false);
  }

  const lengthOk = newPasscode.length >= 8 && newPasscode.length <= 30;
  const noSpaceOk = newPasscode.length > 0 && PASSCODE_RE.test(newPasscode);
  const matchOk = newPasscode.length > 0 && newPasscode === confirmPasscode;

  function submitPasscodeChange() {
    if (!noSpaceOk) { setPasscodeError("Passcode must be 8–30 characters: letters, numbers, or symbols, no spaces."); return; }
    if (!matchOk) { setPasscodeError("Passcodes don't match."); return; }
    setPasscodeError("");
    setConfirmingChange(true);
  }
  function confirmPasscodeChange() {
    // The value we just revealed (or null, for a household that never had one) is what
    // proves to the server we know the current passcode — no extra typing needed from
    // the parent, since they already saw it moments ago via reveal-passcode.
    setPendingVerifiedPasscode(revealedPasscode);
    update((s) => { s.settings.householdPasscode = newPasscode; return s; });
    setRevealedPasscode(newPasscode);
    showToast(ct("toast.household_passcode_updated", "Household passcode updated"), "good");
    setNewPasscode(""); setConfirmPasscode(""); setChanging(false); setConfirmingChange(false);
  }

  // W3-PR2 (approved 27 Aug): the section is now four task-shaped cards, one open at a
  // time (KidDetail's CollapsibleSection + openMap pattern — a screen parents already
  // know). "Add a device" opens by default — it's why people come here — UNLESS the
  // owner email is unconfirmed, in which case that card opens instead: the attention
  // dot led them here, so the screen finishes the journey (proposal 2). Passcode lives
  // ONLY in its own card (the proposal's flagged no-duplication variant): card 1's
  // manual path shows the code and jump-links to it, keeping the reveal single-sourced
  // (and spec locators unambiguous).
  // LB-265 (Alan, 12 Sep): "Why does it default to Owner email expanded on open?" — because
  // an unconfirmed owner email used to seize the default card, on the reasoning that the
  // attention dot had led the parent here so the screen should finish the journey. It did
  // that at the cost of every other visit: adding a device is why people come here. The
  // journey still finishes itself, through the amber line below rather than by taking the
  // screen over — the row keeps its own dot either way.
  // ---- LB-342: the Adults card (Main, A2, A3) ----
  const adultsList = adultsOf(state);
  const pendingInvites = Array.isArray(state.pendingInvites) ? state.pendingInvites : [];
  // Session FIRST, device second. Parent Mode was unlocked by somebody a moment ago and the
  // worker said who; a device minted before part 1 carries no stored owner at all, and reading
  // only that made this card decide nobody was here — which hid the Invite row on precisely
  // the devices most likely to be testing it.
  const myAdultId = getActiveAdultId(code) || getDeviceOwnerAdultId(code);
  // Mirrors the worker's mayManageAdults exactly. This HIDES a control; it is not the
  // control — every one of these calls is checked again server-side against the adult the
  // device record names, which is the only copy a client cannot lie about.
  const canManageAdults = isOwner(state, myAdultId) || can(state, myAdultId, "manageAdults");
  const householdFull = (adultsList.length + pendingInvites.length) >= 4;
  // ---- LB-344: manage adults (E1, E2, E3) ----
  const [openAdult, setOpenAdult] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState("");
  const openAdultRow = openAdult ? adultsList.find((a) => a && a.id === openAdult) || null : null;

  // Mirrors the worker's mayRemove. The Owner is never removable, by anyone including
  // themselves — there is no transfer in this release, so a household with no Owner is one
  // nobody can recover. Leaving needs no permission: it is not power over someone else.
  function canRemove(target) {
    if (!target || target.role === "owner") return false;
    if (myAdultId && target.id === myAdultId) return true;
    return canManageAdults;
  }

  async function doRemove() {
    if (!openAdultRow) return;
    setRemoving(true); setRemoveError("");
    const leaving = openAdultRow.id === myAdultId;
    const res = await apiRemoveAdult(code, openAdultRow.id);
    setRemoving(false);
    if (!res.ok) {
      setRemoveError(res.ownerProtected
        ? ct("adults.remove_err_owner", "The household owner cannot be removed")
        : res.error || ct("adults.remove_err_generic", "Couldn't do that just now. Check your connection and try again"));
      return;
    }
    // Leaving signs THIS device out, so there is nothing left to render — hand straight off
    // to the same exit the Leave household control uses.
    if (leaving && onLeaveHousehold) { onLeaveHousehold(); return; }
    update((st) => { st.adults = (st.adults || []).filter((a) => a && a.id !== openAdultRow.id); return st; });
    setOpenAdult(null); setConfirmRemove(false);
    showToast(ct("toast.adult_removed", "{name} has been removed", { name: adultLabel(openAdultRow) }), "good");
  }

  async function rotatePasscode() {
    setRemoving(true);
    const res = await apiRotatePasscode(code);
    setRemoving(false);
    if (!res.ok) { showToast(ct("toast.passcode_rotate_failed", "Couldn't change the passcode just now"), "bad"); return; }
    showToast(ct("toast.passcode_rotated", "Household passcode changed"), "good");
  }

  const [inviting, setInviting] = useState(false);
  const [invName, setInvName] = useState("");
  const [invEmail, setInvEmail] = useState("");
  const [invError, setInvError] = useState("");
  const [invBusy, setInvBusy] = useState(null);

  async function sendInvite() {
    setInvBusy("new"); setInvError("");
    const res = await apiInviteAdult(code, { name: invName.trim(), email: invEmail.trim() });
    setInvBusy(null);
    if (!res.ok) {
      // Each refusal says what is actually wrong. "Something went wrong" on a screen whose
      // whole job is one form is the shape of error principle 5 rules out.
      setInvError(
        res.duplicate ? ct("adults.invite_err_duplicate", "That address is already on this household")
          : res.full ? ct("adults.invite_err_full", "A household can have 4 adults")
          : res.notAllowed ? ct("adults.invite_err_not_allowed", "Only an adult who can manage adults may invite one")
          : ct("adults.invite_err_generic", "Couldn't send that invite just now. Check your connection and try again")
      );
      return;
    }
    setInviting(false);
    showToast(ct("toast.invite_sent", "Invite sent to {email}", { email: invEmail.trim() }), "good");
    // REPLACED with the server's list, never appended to. pendingInvites is server-owned:
    // the save path keeps the stored copy and ignores the one a client sends. An appended
    // local echo therefore survives only until a save rebases onto the server state that
    // already holds the same invite — and then the row is there twice until the next
    // refresh (Alan, staging, 19 Sep). Replacing is idempotent however many rebases follow.
    if (res.invites) update((st) => { st.pendingInvites = res.invites; return st; });
  }

  async function actOnInvite(id, action) {
    setInvBusy(id);
    const res = await apiInviteAction(code, id, action);
    setInvBusy(null);
    if (!res.ok) { showToast(ct("toast.invite_failed", "Couldn't do that just now. Check your connection and try again"), "bad"); return; }
    showToast(action === "resend" ? ct("toast.invite_resent", "Invite sent again") : ct("toast.invite_cancelled", "Invite cancelled"), "good");
    // Same rule as sendInvite: replace with the server's list, never edit our own copy.
    if (res.invites) update((st) => { st.pendingInvites = res.invites; return st; });
  }

  // The grantable permission goes through its own endpoint and the list comes back from the
  // server. It used to be an update() straight into household state, which could not have
  // worked twice over: adults are server-owned on save, so the change was never persisted —
  // and the updater read `const a = (st.adults || []).find((x) => x.id === a.id)`, where `a`
  // on the right is the one being declared on the left. That is a ReferenceError thrown
  // inside a React state updater, which unmounts the tree: the blank screen Alan saw.
  const [permBusy, setPermBusy] = useState(null);
  async function setPermission(adultId, value) {
    setPermBusy(adultId);
    const res = await apiSetAdultPermission(code, adultId, "manageAdults", value);
    setPermBusy(null);
    if (!res.ok) {
      showToast(res.ownerOnly
        ? ct("toast.permission_owner_only", "Only the household owner can change that")
        : ct("toast.permission_failed", "Couldn't save that just now. Check your connection and try again"), "bad");
      return;
    }
    // Replaced, not edited — the same rule the invite list follows.
    if (res.adults) update((st) => { st.adults = res.adults; return st; });
  }

  // (4) Nothing opens by default. "Add a device" claimed that slot when it was the only
  // reason to visit this screen; with Adults here too, opening one card guesses which errand
  // a parent is on, and guesses wrong half the time. The deep link from elsewhere still opens
  // a specific card — see setOpenCard callers.
  const [openCard, setOpenCard] = useState(null);
  // ---- LB-341: Add a device on one-time tickets (F1, F2, F3) ----
  const [ticketFor, setTicketFor] = useState("shared"); // F1's "This is for"; shared is the safe default
  const [ticketRoute, setTicketRoute] = useState(null); // null | "qr" | "link"
  const [ticket, setTicket] = useState(null);
  const [makingTicket, setMakingTicket] = useState(false);
  const [actingOn, setActingOn] = useState(null);

  // "regenerated each time the screen opens" — the spec's words. A ticket left on screen from
  // a previous visit is a live credential nobody is watching, so opening a route always makes
  // a fresh one and closing the card drops it.
  async function openTicketRoute(route) {
    setTicketRoute(route);
    setTicket(null);
    setMakingTicket(true);
    const issued = await apiIssueJoinTicket(code, { forOwnDevice: ticketFor === "mine" });
    setMakingTicket(false);
    if (!issued.ok) {
      // The server's own words when it has any. Only a fetch that never arrived is a
      // connection problem, and only that one says so (principle 5).
      showToast(
        issued.networkError
          ? ct("toast.add_device_offline", "Couldn't reach the server. Check your connection and try again")
          : issued.error || ct("toast.add_device_failed", "Couldn't make a code just now"),
        "bad",
      );
      setTicketRoute(null);
      return;
    }
    setTicket(issued);
  }

  function expiryWords(expiresAt) {
    if (!expiresAt) return "";
    const d = new Date(expiresAt);
    const time = d.toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit" }).toLowerCase();
    const sameDay = d.toDateString() === new Date().toDateString();
    return sameDay ? `at ${time} today` : `at ${time} tomorrow`;
  }

  async function shareTicket() {
    if (!ticket) return;
    const url = joinTicketLink(code, ticket.ticket);
    const text = ct("settings.household.share_text", "Join our Little Bankers household: {url}", { url });
    // Native Run C (LB-392): the share sheet through src/lib/share.js — the app's own, the
    // browser's where it has one, the clipboard otherwise. A dismissed sheet is not a failure
    // worth a toast, nor is a copy failure here (the Copy button beside it says its own).
    const r = await shareText(text);
    if (r === "copied") showToast(ct("toast.link_copied", "Link copied"), "good");
  }

  async function copyTicket() {
    if (!ticket) return;
    try {
      await navigator.clipboard.writeText(joinTicketLink(code, ticket.ticket));
      showToast(ct("toast.link_copied", "Link copied"), "good");
    } catch { showToast(ct("toast.copy_failed", "Couldn't copy. Select and copy it manually"), "bad"); }
  }

  // F3. Polled only while this card is open and only for this adult's own requests — the
  // worker does that filtering, because "only the adult who issued it can approve it" has to
  // hold on the server whatever a client chooses to show.
  // One surface (Alan, 22 Sep). This card used to poll every five seconds and render its own
  // approval list, while ParentView polled every twenty and raised the overlay — two pollers,
  // two UIs, and whichever won the race is what the parent saw. On the Add-a-device card the
  // five-second one always won, so the overlay looked broken when it was only slower.
  //
  // The list is gone; the overlay is the surface. All this does now is tell ParentView that a
  // parent is sitting on the screen they just sent a link from, so the overlay's poll runs at
  // the rate this one used to rather than making them wait longer than before.
  useEffect(() => {
    if (!onAwaitingJoin) return undefined;
    onAwaitingJoin(openCard === "add");
    return () => onAwaitingJoin(false);
  }, [openCard, onAwaitingJoin]);


  // Canvas C2 groups these two under "Only you". Every control inside them is Owner-only on
  // the SERVER already — reveal-passcode and reveal-jointoken both return 403 ownerOnly, and
  // the owner email changes only through the confirm-link loop — so an Adult opening either
  // card could only ever reach an error. Offering a card that can only refuse reads as a
  // fault rather than a boundary, and it contradicts the adult-detail footnote two taps away.
  // The gates stay exactly as they are; this stops the screen promising what they will deny.
  //
  // UNKNOWN IS NOT "NOT THE OWNER". The first cut was isOwner(state, myAdultId) alone, and the
  // mounted walk caught what a source-shape assertion could not: when this device has not
  // identified its adult, myAdultId is null, isOwner is false, and the OWNER loses their own
  // two cards. That is the preview mount and, in the wild, the session that just created the
  // household — both of them the Owner.
  //
  // So it hides only when we positively know this is somebody else. Nothing leaks by being
  // shown: every control inside both cards is refused server-side for a non-Owner, and since
  // verify-pin records the adult on the device (LB-312, 19 Sep) a real Adult in Parent Mode is
  // always known. The unknown case is the Owner's, and guessing against them is the one
  // failure mode worth avoiding here.
  const ownerOnlyCards = !myAdultId || isOwner(state, myAdultId);

  // The permission switch follows the same rule, and for the same reason: a device that has
  // not identified its adult is the Owner's — the preview mount, and the session that just
  // created the household. Disabling on `!isOwner(state, myAdultId)` alone left the Owner
  // looking at their own switch, greyed out, on the device they made the household with
  // (Alan, 19 Sep: "Permissions switch is present for Adult, but it's not active").
  // Failing open costs nothing: the server refuses a non-Owner with ownerOnly, and
  // setPermission already turns that into the "only the household owner" toast.
  const mayGrantPermission = !myAdultId || isOwner(state, myAdultId);

  const cardToggle = (id) => () => setOpenCard((cur) => (cur === id ? null : id));

  return (
    <div className="flex flex-col gap-3">
      {/* Goes with the card it opens: an Adult tapping this would land on nothing. */}
      {ownerOnlyCards && state.ownerEmail && !state.ownerEmailVerified && openCard !== "email" && (
        <button
          type="button"
          data-testid="household-email-unconfirmed"
          onClick={() => setOpenCard("email")}
          className="w-full text-left bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs font-semibold text-amber-800"
        >
          {ct("settings.household.email_unconfirmed_line", "Your owner email is not confirmed yet.")} <span className="underline">{ct("settings.household.email_unconfirmed_open", "Open Owner email")}</span>
        </button>
      )}
      <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-200">
      {/* LB-265: the first card is now about the QR and nothing else. Its opening paragraph
          used to spend its first clause on the already-installed case, which is not why
          anyone opens this screen; that is a footnote below the button now. The code and
          passcode moved out entirely into their own card — they were a fold-out at the
          bottom of this one, which is how the two things a parent needs together ended up
          never being together. */}
      {/* LB-342 (Main, A2, A3) — the Adults card. Visible to every adult, because knowing who
          else is in the household is not a privilege; the Invite row is the part gated on
          `manageAdults`, which only the Owner may grant. */}
      <CollapsibleSection variant="row" icon={<Icon id="ic-family" size={18} />} title={ct("adults.card", "Adults")} open={openCard === "adults"} onToggle={cardToggle("adults")} summary={<p className="text-xs text-slate-500">{ct("adults.card_sum", "Who can use Parent Mode")}</p>}>
        <div className="pt-1" data-testid="adults-card">
          <p className="text-base text-slate-500 mb-2">{ct("adults.card_body", "Each adult uses Parent Mode with their own PIN and gets their own emails.")}</p>
          <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-200 mb-2">
            {/* (3) An expanded adult opens directly beneath ITS OWN row. The detail used to
                render after the whole list and the Invite button, so tapping the first of
                three adults scrolled you past the other two and past Invite to find it —
                which reads as a different screen rather than a row opening. */}
            {adultsList.map((a) => (
              <div key={a.id}>
                <button type="button" className="w-full flex items-center gap-3 px-3 py-2.5 text-left" data-testid="adult-row" aria-expanded={openAdult === a.id} onClick={() => { setOpenAdult(openAdult === a.id ? null : a.id); setConfirmRemove(false); setRemoveError(""); }}>
                  <span className="w-9 h-9 flex-shrink-0 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-bold text-sm">{(adultLabel(a) || "?").slice(0, 1).toUpperCase()}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-slate-800">{adultLabel(a) || ct("adults.row_unnamed", "A parent")}</span>
                    <span className="block text-xs text-slate-500 font-medium">
                      {a.role === "owner" ? ct("adults.row_owner", "Owner") : ct("adults.row_adult", "Adult")}
                      {a.id === myAdultId ? ct("adults.row_you", " · you") : ""}
                    </span>
                  </span>
                  <Icon id={openAdult === a.id ? "ic-chevron-up" : "ic-chevron-right"} size={16} />
                </button>
                {openAdult === a.id && (
                  <div className="px-3 pb-3">

          {/* E1 — an adult's detail, and the two ways out of the household. */}
          
            <div className="bg-white border border-slate-200 rounded-xl p-3 mt-2" data-testid="adult-detail">
              <div className="flex items-center gap-2 mb-2">
                <button type="button" className="text-xs font-bold text-slate-500" data-testid="adult-detail-back" onClick={() => setOpenAdult(null)}>{ct("adults.detail_back", "Back")}</button>
                <span className="text-sm font-bold text-slate-800 flex-1">{adultLabel(a) || ct("adults.row_unnamed", "A parent")}</span>
              </div>
              <div className="bg-slate-50 border border-slate-100 rounded-lg divide-y divide-slate-200 mb-2">
                <div className="flex px-3 py-2"><span className="flex-1 text-xs font-semibold text-slate-700">{ct("adults.detail_email", "Email")}</span><span className="text-xs text-slate-500 font-medium truncate">{a.email || "—"}</span></div>
                <div className="flex px-3 py-2"><span className="flex-1 text-xs font-semibold text-slate-700">{ct("adults.detail_joined", "Joined")}</span><span className="text-xs text-slate-500 font-medium">{a.addedAt ? fmtTimestamp(a.addedAt) : ct("adults.detail_joined_start", "At the start")}</span></div>
              </div>

              {/* The single grantable permission. Only the Owner may set it — an adult with
                  manageAdults can invite and remove, but cannot hand that power on. */}
              {a.role !== "owner" && (
                <div className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2 mb-2">
                  <span className="flex-1 text-sm font-semibold text-slate-800">{ct("adults.detail_permission", "Invite and remove adults")}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!!a.permissions?.manageAdults}
                    aria-label={ct("adults.detail_permission", "Invite and remove adults")}
                    data-testid="adult-permission"
                    disabled={!mayGrantPermission || permBusy === a.id}
                    className={`relative w-11 h-6 flex-shrink-0 rounded-full ${a.permissions?.manageAdults ? "bg-slate-800" : "bg-slate-300"} ${mayGrantPermission ? "" : "opacity-40"}`}
                    onClick={() => setPermission(a.id, !a.permissions?.manageAdults)}
                  >
                    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${a.permissions?.manageAdults ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </div>
              )}
              {/* (2) Three short bullets, caption size and muted. One paragraph carrying
                  three unrelated facts made a footnote read like terms and conditions —
                  nobody reads it, so the one sentence that matters (what only the Owner can
                  do) was buried in the middle of the other two. */}
              <ul className="text-[11px] leading-4 text-slate-400 font-medium mb-2 space-y-1" data-testid="adult-detail-footnote">
                {/* The Owner's OWN row says what only the Owner can do, to the Owner. The
                    third-person list below belongs on somebody else's row: "They can use
                    everything in Parent Mode" is a strange thing to read about yourself, and
                    the two things it names are true of every adult, so on this row it says
                    nothing at all. What is worth knowing here is what dies with this account. */}
                {a.role === "owner" ? (
                  <>
                    <li>{ct("adults.detail_note_you_secrets", "Only you can see the household code and passcode.")}</li>
                    <li>{ct("adults.detail_note_you_email", "Only you can change the owner email.")}</li>
                    <li>{ct("adults.detail_note_you_permission", "Only you can choose who else may invite and remove adults.")}</li>
                    <li>{ct("adults.detail_note_you_delete", "Only you can delete the household.")}</li>
                  </>
                ) : (
                  <>
                    <li>{ct("adults.detail_note_parent", "They can use everything in Parent Mode.")}</li>
                    <li>{ct("adults.detail_note_devices", "They can add devices with a one-time code.")}</li>
                    <li>{ct("adults.detail_note_owner", "Only you can see the household passcode and join code, change the owner email, delete the household, or remove yourself from it.")}</li>
                  </>
                )}
              </ul>

              {/* E2 and E3. The Owner's own row offers neither: there is no transfer in this
                  release, so a household without an Owner is one nobody can recover. */}
              {a.role !== "owner" && canRemove(a) && !confirmRemove && (
                <button className="w-full bg-rose-50 text-rose-700 font-bold text-sm rounded-xl py-2.5" data-testid="adult-remove-open" onClick={() => setConfirmRemove(true)}>
                  {a.id === myAdultId
                    ? ct("adults.leave_open", "Leave this household")
                    : ct("adults.remove_open", "Remove {name} from the household", { name: adultLabel(a) })}
                </button>
              )}

              {confirmRemove && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="adult-remove-confirm">
                  <p className="text-sm font-bold text-rose-800 mb-1">
                    {a.id === myAdultId ? ct("adults.leave_title", "Leave this household?") : ct("adults.remove_title", "Remove {name}?", { name: adultLabel(a) })}
                  </p>
                  <p className="text-xs text-rose-700 mb-2">{ct("adults.remove_body", "Their devices are signed out and their PIN stops working. Shared family devices keep working with your PIN.")}</p>
                  <p className="text-xs text-rose-700 mb-2">{ct("adults.remove_body_email", "We will email them to say so, and the join code is replaced so an old QR cannot bring them back.")}</p>
                  {/* Owner-only, and its own action — a cancel of the removal must not rotate
                      the passcode, and a removal must not force it. */}
                  {isOwner(state, myAdultId) && (
                    <div className="flex items-center gap-2 bg-white border border-rose-200 rounded-lg px-3 py-2 mb-2">
                      <span className="flex-1 min-w-0">
                        <span className="block text-xs font-semibold text-slate-800">{ct("adults.remove_passcode", "Also change the household passcode")}</span>
                        <span className="block text-xs text-slate-500">{ct("adults.remove_passcode_why", "They still know the old one")}</span>
                      </span>
                      <button className="bg-slate-100 text-slate-700 font-bold text-xs rounded-lg px-3 py-1.5" data-testid="adult-rotate-passcode" disabled={removing} onClick={rotatePasscode}>{ct("adults.remove_passcode_cta", "Change")}</button>
                    </div>
                  )}
                  {removeError && <p className="text-xs text-rose-700 font-bold mb-2" data-testid="adult-remove-error">{removeError}</p>}
                  <button className="w-full bg-rose-600 text-white font-bold text-sm rounded-xl py-2.5 mb-2" data-testid="adult-remove-confirm-go" disabled={removing} onClick={doRemove}>
                    {a.id === myAdultId ? ct("adults.leave_cta", "Leave the household") : ct("adults.remove_cta", "Remove {name}", { name: adultLabel(a) })}
                  </button>
                  <button className="w-full text-xs font-bold text-slate-500 py-1.5" disabled={removing} onClick={() => setConfirmRemove(false)}>{ct("settings.cancel_button", "Cancel")}</button>
                </div>
              )}
            </div>
                  </div>
                )}
              </div>
            ))}
            {/* A3 — a pending invite sits in the same list, because to a parent looking at
                this screen it is the same question: who is in this household? */}
            {pendingInvites.map((p) => (
              <div key={p.id} className="px-3 py-2.5" data-testid="pending-invite">
                <div className="flex items-center gap-3">
                  <span className="w-9 h-9 flex-shrink-0 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center font-bold text-sm">{(p.name || "?").slice(0, 1).toUpperCase()}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-slate-800">{p.name} <span className="text-xs font-bold text-amber-600">{ct("adults.invite_pending", "Invited")}</span></span>
                    <span className="block text-xs text-slate-500 font-medium truncate">{ct("adults.invite_expiry", "{email} · link expires in 24 hours", { email: p.email })}</span>
                  </span>
                </div>
                {canManageAdults && (
                  <div className="flex gap-2 mt-2">
                    <button className="flex-1 bg-slate-100 text-slate-600 font-bold text-xs rounded-lg py-1.5" data-testid="invite-resend" disabled={invBusy === p.id} onClick={() => actOnInvite(p.id, "resend")}>{ct("adults.invite_resend", "Resend")}</button>
                    <button className="flex-1 bg-white border border-slate-200 text-slate-600 font-bold text-xs rounded-lg py-1.5" data-testid="invite-cancel" disabled={invBusy === p.id} onClick={() => actOnInvite(p.id, "cancel")}>{ct("adults.invite_cancel", "Cancel invite")}</button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* A2 — only for an adult who may manage adults. The server checks the same thing;
              this hides a control rather than being the control. */}
          {canManageAdults && !inviting && (
            <button className="w-full bg-slate-100 text-slate-700 font-bold text-sm rounded-xl py-2.5" data-testid="invite-adult-open" disabled={householdFull} onClick={() => { setInviting(true); setInvName(""); setInvEmail(""); setInvError(""); }}>
              {householdFull ? ct("adults.invite_full", "A household can have 4 adults") : ct("adults.invite_open", "Invite an adult")}
            </button>
          )}

          {canManageAdults && inviting && (
            <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="invite-form">
              <div className="text-sm font-bold text-slate-800 mb-1">{ct("adults.invite_title", "Invite an adult")}</div>
              <p className="text-xs text-slate-500 mb-2">{ct("adults.invite_body", "They get Parent Mode on their own device, with their own PIN.")}</p>
              <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="inv-name">{ct("adults.invite_name", "Name")}</label>
              <input id="inv-name" className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full mb-2" value={invName} maxLength={40} onChange={(e) => setInvName(e.target.value)} data-testid="invite-name" />
              <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="inv-email">{ct("adults.invite_email", "Email address")}</label>
              <input id="inv-email" type="email" inputMode="email" className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full mb-2" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} data-testid="invite-email" />
              {invError && <p className="text-xs text-rose-600 font-semibold mb-2" data-testid="invite-error">{invError}</p>}
              <p className="text-xs text-slate-500 mb-2">{ct("adults.invite_note", "We'll email them a link. It works once and lasts 24 hours.")}</p>
              <button className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 mb-2" data-testid="invite-send" disabled={invBusy === "new" || !invName.trim() || !invEmail.trim()} onClick={sendInvite}>{ct("adults.invite_send", "Send invite")}</button>
              <button className="w-full text-xs font-bold text-slate-500 py-1.5" onClick={() => setInviting(false)}>{ct("settings.cancel_button", "Cancel")}</button>
            </div>
          )}

        </div>
      </CollapsibleSection>

      <CollapsibleSection variant="row" icon={<Icon id="ic-devices" size={18} />} title={ct("settings.household.card_add", "Add a device")} open={openCard === "add"} onToggle={cardToggle("add")} summary={<p className="text-xs text-slate-500">{ct("settings.household.card_add_sum", "Scan a QR to join")}</p>}>
        <div className="pt-1">
        {/* LB-341 (F1). The QR used to encode settings.householdJoinToken — a STANDING
            credential, so every photograph of it stayed a way in for ever. It encodes a
            one-time ticket now, regenerated each time this card opens, and a ticket only
            buys a pending request the issuing adult has to approve (F3). The join token
            survives behind the Owner-only reveal and nothing else reads it. */}
        <p className="text-base text-slate-500 mb-2">{ct("settings.household.add_ticket_body", "A one-time code that works for 24 hours, then expires. It only ever adds one device.")}</p>

        {/* THE ONE CONTROL THE CANVAS DOES NOT DRAW (named in the spec, and in the report).
            It decides the minted device's ownerAdultId, which decides whose PIN Parent Mode
            asks for on it — so it has to be answered before the code is made, not after. */}
        <div className="text-sm font-semibold text-slate-700 mb-1">{ct("settings.household.add_for_label", "This is for")}</div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-3" role="group" aria-label={ct("settings.household.add_for_label", "This is for")}>
          {[["mine", ct("settings.household.add_for_mine", "My own device")], ["shared", ct("settings.household.add_for_shared", "A child or shared device")]].map(([val, label]) => (
            <button
              key={val}
              type="button"
              aria-pressed={ticketFor === val}
              data-testid={`add-device-for-${val}`}
              className={`flex-1 text-xs font-bold rounded-md py-2 ${ticketFor === val ? "bg-white text-slate-800 shadow-sm" : "text-slate-500"}`}
              onClick={() => { setTicketFor(val); setTicket(null); setTicketRoute(null); }}
            >{label}</button>
          ))}
        </div>

        {!ticketRoute && (
          <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-200 mb-2">
            <button type="button" data-testid="add-device-route-qr" className="w-full flex items-center gap-3 px-3 py-2.5 text-left" onClick={() => openTicketRoute("qr")} disabled={makingTicket}>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-slate-800">{ct("settings.household.add_route_qr", "Show a code here")}</span>
                <span className="block text-xs text-slate-500 font-medium">{ct("settings.household.add_route_qr_sum", "For a phone or tablet in the room")}</span>
              </span>
              <Icon id="ic-chevron-right" size={16} />
            </button>
            <button type="button" data-testid="add-device-route-link" className="w-full flex items-center gap-3 px-3 py-2.5 text-left" onClick={() => openTicketRoute("link")} disabled={makingTicket}>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-semibold text-slate-800">{ct("settings.household.add_route_link", "Send a link")}</span>
                <span className="block text-xs text-slate-500 font-medium">{ct("settings.household.add_route_link_sum", "For a device that is somewhere else")}</span>
              </span>
              <Icon id="ic-chevron-right" size={16} />
            </button>
          </div>
        )}

        {ticketRoute === "qr" && (
          <div className="flex flex-col items-center bg-slate-50 border border-slate-100 rounded-xl p-4 mb-2" data-testid="add-device-qr">
            {ticket ? (
              <img src={joinQrDataUrl(joinTicketLink(code, ticket.ticket))} alt={ct("settings.household.add_qr_alt", "One-time code to add a device")} className="w-44 h-44 bg-white rounded-lg p-2 border border-slate-200" />
            ) : (
              <div className="w-44 h-44 flex items-center justify-center text-sm text-slate-400">…</div>
            )}
            {ticket && <div className="text-lg font-extrabold tracking-widest text-slate-800 mt-2">{ticket.display}</div>}
            <p className="text-xs text-slate-400 font-medium mt-1 text-center">{ct("settings.household.add_qr_note", "Scan it with the other device's camera. You will be asked to approve it.")}</p>
          </div>
        )}

        {/* F2 */}
        {ticketRoute === "link" && (
          <div className="mb-2" data-testid="add-device-link">
            <p className="text-base text-slate-500 mb-2">{ct("settings.household.link_body", "Send this to the device you want to add. It works once, and it expires in 24 hours.")}</p>
            <div className="bg-white border border-slate-200 rounded-lg p-3 text-center mb-2">
              <div className="text-xs text-slate-500 font-semibold">{ticket ? joinTicketDisplayLink("") : ""}</div>
              <div className="text-xl font-extrabold tracking-widest text-slate-800">{ticket ? ticket.display : "…"}</div>
              {ticket && <div className="text-xs text-slate-500 font-medium mt-1">{ct("settings.household.link_expires", "Expires {when}", { when: expiryWords(ticket.expiresAt) })}</div>}
            </div>
            <button className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 mb-2" onClick={shareTicket} disabled={!ticket}>{ct("settings.household.link_share", "Share the link")}</button>
            <button className="w-full bg-slate-100 text-slate-600 font-bold text-sm rounded-xl py-2.5" onClick={copyTicket} disabled={!ticket}>{ct("settings.copy_button", "Copy")}</button>
            <p className="text-xs text-slate-500 mt-2">{ct("settings.household.link_approve_note", "You will be asked to approve the device before it can be used.")}</p>
          </div>
        )}

        {ticketRoute && (
          <button className="w-full text-xs font-bold text-slate-500 underline underline-offset-2 mb-2" onClick={() => { setTicketRoute(null); setTicket(null); }}>{ct("settings.household.add_back", "Back to both ways")}</button>
        )}


        <p className="text-xs text-slate-500 mt-2">{ct("settings.household.card_add_installed", "Already have the app on that device? Open it and tap Scan QR to join on its Join screen.")}</p>
        {/* Same: the link inside Add a device opens the passcode card, so it is the Owner's
            too. Without this an Adult taps it and the screen simply does nothing. */}
        {ownerOnlyCards && <button className="w-full text-xs font-bold text-slate-500 underline underline-offset-2 mt-2" data-testid="household-show-code" onClick={() => setOpenCard("passcode")}>{ct("settings.household.manual_toggle", "Show household code and passcode")}</button>}
        <p className="text-xs text-slate-400 font-medium mt-3">{ct("settings.household.devices_link_hint", "Every joined device shows under")} <button className="underline font-bold" onClick={() => onOpenCategory && onOpenCategory("devices")} data-testid="household-devices-hint">{ct("settings.household.devices_link", "Settings → Devices")}</button></p>
        </div>
      </CollapsibleSection>

      {/* LB-265: one card holding BOTH secrets. House style is sentence case and the word
          "and", so the ticket's "code & passcode" is written out. The link at the foot of
          Add a device opens exactly this card. */}
      {/* LB-370 (Alan, 24 Sep; canvas signed off): Invite a friend lives here permanently, one
          more row in this card after Add a device. Visible to every adult; nothing on it is
          Owner-only. The count comes from the worker's referral record, never from state. */}
      <CollapsibleSection variant="row" icon={<Icon id="ic-sent" size={18} />} title={ct("settings.referral.card", "Invite a friend")} open={openCard === "referral"} onToggle={(v) => { cardToggle("referral")(); if (v) openReferral(); }} testid="household-referral" summary={<p className="text-xs text-slate-500">{referral && referral.joinedCount > 0
        ? ct("settings.referral.card_sum_joined", "Share your link · {count} friend{plural} joined", { count: referral.joinedCount, plural: referral.joinedCount === 1 ? " has" : "s have" })
        : ct("settings.referral.card_sum", "Share your link with a family who would like this")}</p>}>
        <div className="pt-1" data-testid="referral-card">
          <p className="text-base text-slate-500 mb-3">{ct("settings.referral.body", "Know a family who would like Little Bankers? Share your link and they go straight in, no waitlist.")}</p>
          {referralError && <p className="text-xs text-rose-600 font-semibold mb-2" data-testid="referral-error">{referralError}</p>}
          {referralBusy && !referral?.minted && <p className="text-xs text-slate-400 font-medium mb-2">{ct("settings.referral.minting", "Making your link…")}</p>}
          {referral && referral.minted && (
            <>
              <div className="text-sm font-semibold text-slate-700 mb-1">{ct("settings.referral.link_label", "Your link")}</div>
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 min-w-0 text-sm font-semibold text-slate-800 bg-slate-50 rounded-lg py-2 px-3 border border-slate-100 truncate" data-testid="referral-link">{referralShareLink(referral.inviteCode).replace(/^https?:\/\//, "").replace(/&utm_.*$/, "")}</div>
                <button className="bg-slate-100 text-slate-600 font-bold text-xs rounded-lg px-3 py-2.5 flex-shrink-0" data-testid="referral-copy" onClick={() => shareReferralLink("copy")}>{ct("settings.referral.copy", "Copy")}</button>
              </div>
              <button className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-2.5 mb-2" data-testid="referral-share" onClick={() => shareReferralLink("share")}>{ct("settings.referral.share", "Share your link")}</button>
              <p className="text-xs text-slate-500 mb-3">{referral.disabled
                ? ct("settings.referral.uses_disabled", "This link is switched off at the moment.")
                : ct("settings.referral.uses", "Your link can be used by {max} households. {left} use{plural} left.", { max: referral.maxUses, left: referral.usesLeft, plural: referral.usesLeft === 1 ? "" : "s" })}</p>
              <div className="flex items-center justify-between bg-white border border-slate-200 rounded-lg px-3 py-2.5 mb-3">
                <span className="text-sm font-semibold text-slate-800">{ct("settings.referral.joined_label", "Friends who have joined")}</span>
                <span className="text-lg font-bold text-slate-900" data-testid="referral-joined">{referral.joinedCount}</span>
              </div>
              <div className="text-xs font-semibold text-slate-500 mb-1">{ct("settings.referral.what_label", "What your friend sees")}</div>
              <p className="text-xs text-slate-500">{ct("settings.referral.what_body", "The Little Bankers page with your code already in the box. They tap Get started and set up their own household. You never see their details, and they never see yours.")}</p>
            </>
          )}
        </div>
      </CollapsibleSection>

      {ownerOnlyCards && (
      <CollapsibleSection variant="row" icon={<Icon id="ic-pin" size={18} />} title={ct("settings.household.card_passcode", "Household code and passcode")} open={openCard === "passcode"} onToggle={cardToggle("passcode")} summary={<p className="text-xs text-slate-500">{ct("settings.household.card_passcode_sum", "For joining without a QR")}</p>}>
        <div className="pt-1">
        <div className="text-sm font-semibold text-slate-700 mb-1">{ct("settings.household.code_label", "Household code")}</div>
        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 text-center text-lg font-bold tracking-widest text-slate-800 bg-slate-50 rounded-lg py-2 border border-slate-100">{code}</div>
          <button
            className="bg-slate-100 text-slate-600 font-bold text-xs rounded-lg px-3 py-2.5 flex-shrink-0"
            onClick={async () => {
              try { await navigator.clipboard.writeText(code); showToast(ct("toast.code_copied", "Code copied"), "good"); }
              catch { showToast(ct("toast.copy_failed", "Couldn't copy. Select and copy it manually"), "bad"); }
            }}
          >{ct("settings.copy_button", "Copy")}</button>
        </div>
        <div className="text-sm font-semibold text-slate-700 mb-1">{ct("settings.household.passcode_label", "Passcode")}</div>
        <div className="flex items-center gap-2 mb-1.5">
          <div className="flex-1 text-center text-base font-semibold tracking-wide text-slate-800 bg-slate-50 rounded-lg py-2 border border-slate-100 font-mono">
            {revealing ? "…" : showPasscode ? (revealedPasscode || "Not set yet") : "•".repeat(Math.max(8, (revealedPasscode || "").length || 10))}
          </div>
          <button className="bg-slate-100 text-slate-600 font-bold text-xs rounded-lg px-3 py-2.5 flex-shrink-0" onClick={toggleShowPasscode} disabled={revealing}>
            {showPasscode ? "Hide" : "Show"}
          </button>
          <button className="bg-slate-100 text-slate-600 font-bold text-xs rounded-lg px-3 py-2.5 flex-shrink-0" onClick={copyPasscode} disabled={revealing}>{ct("settings.copy_button", "Copy")}</button>
        </div>
        <p className="text-base text-slate-500 mb-3">{ct("settings.household.codepass_body", "A joining device needs both of these. Keep them as private as you would a door key.")}</p>

        {/* LB-341: the standing quick-join QR moved HERE from Add a device, which is on
            one-time tickets now. The spec's sentence is "the standing join token stays,
            behind the Owner-only reveal from part 1" — and this card IS that reveal, so this
            is where it belongs. Deleting it instead would have taken the only way to ROTATE
            the token when a QR leaks, which is a security control, not a screen. */}
        <div className="border-t border-slate-100 pt-3">
          <div className="text-sm font-semibold text-slate-700 mb-1">{ct("settings.household.jointoken_label", "Permanent join QR")}</div>
          <p className="text-xs text-slate-500 mb-2">{ct("settings.household.jointoken_body", "The old way in, kept for a device that cannot use a one-time code. It never expires, so treat a photo of it as a key.")}</p>
          {showQr ? (
            <div className="flex flex-col items-center bg-slate-50 border border-slate-100 rounded-xl p-4 mb-2">
              {joinToken && (
                <img src={joinQrDataUrl(joinDeepLink(code, joinToken))} alt={`QR code to quick-join household ${code}`} className="w-44 h-44 bg-white rounded-lg p-2 border border-slate-200" />
              )}
            </div>
          ) : (
            <button className="w-full bg-slate-100 text-slate-600 font-bold text-sm rounded-xl py-2.5 mb-2" onClick={toggleShowQr} disabled={revealingQr}>
              {revealingQr ? "…" : "Show QR"}
            </button>
          )}
          {showQr && (
            <div className="flex gap-2 mb-2">
              <button className="flex-1 bg-slate-100 text-slate-600 font-bold text-xs rounded-lg py-1.5" onClick={toggleShowQr}>{ct("settings.hide_button", "Hide")}</button>
              {!confirmingRegen ? (
                <button className="flex-1 bg-white border border-slate-200 text-slate-600 font-bold text-xs rounded-lg py-1.5" onClick={() => setConfirmingRegen(true)}>{ct("settings.regenerate_qr", "Regenerate")}</button>
              ) : (
                <div className="flex-1 bg-amber-50 border border-amber-200 rounded-lg p-2">
                  <p className="text-xs text-amber-700 font-semibold mb-1.5">{ct("settings.regenerate_qr_warning", "Old QR (and any screenshot of it) stops working. Your code and passcode above are unaffected.")}</p>
                  <div className="flex gap-1.5">
                    <button className="flex-1 bg-slate-800 text-white text-xs font-bold rounded-lg py-1" onClick={confirmRegenerateQr}>{ct("settings.regenerate_qr_confirm", "Yes, regenerate")}</button>
                    <button className="flex-1 bg-white border border-slate-200 text-xs font-bold rounded-lg py-1" onClick={() => setConfirmingRegen(false)}>{ct("settings.cancel_button", "Cancel")}</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>


        {!changing ? (
          <button className="w-full bg-slate-800 text-white font-bold text-sm rounded-xl py-2.5" onClick={openChange} disabled={revealing}>
            Change passcode
          </button>
        ) : !confirmingChange ? (
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
            <div className="text-sm font-semibold text-slate-700 mb-2">{ct("settings.change_passcode_title", "Change household passcode")}</div>
            <input
              className="border border-slate-200 rounded-lg px-3 py-2 text-base w-full mb-2 font-mono"
              placeholder="New passcode"
              type="text"
              maxLength={30}
              value={newPasscode}
              onChange={(e) => setNewPasscode(e.target.value)}
            />
            <input
              className="border border-slate-200 rounded-lg px-3 py-2 text-base w-full mb-2 font-mono"
              placeholder="Confirm new passcode"
              type="text"
              maxLength={30}
              value={confirmPasscode}
              onChange={(e) => setConfirmPasscode(e.target.value)}
            />
            <StrengthMeter passcode={newPasscode} />
            <div className="flex flex-col gap-1 mb-3">
              <RuleCheck ok={lengthOk && noSpaceOk}>{ct("settings.passcode_length_hint", "8–30 characters")}</RuleCheck>
              <RuleCheck ok={noSpaceOk}>Letters, numbers or symbols — no spaces</RuleCheck>
              <RuleCheck ok={matchOk}>{ct("settings.passcodes_match", "Passcodes match")}</RuleCheck>
            </div>
            {passcodeError && <div className="text-rose-600 text-base font-semibold mb-2">{passcodeError}</div>}
            <div className="flex gap-2">
              <button className="flex-1 bg-slate-800 text-white text-xs font-bold rounded-lg py-1.5" onClick={submitPasscodeChange}>{ct("settings.save_new_passcode", "Save new passcode")}</button>
              <button className="flex-1 bg-white border border-slate-200 text-xs font-bold rounded-lg py-1.5" onClick={() => setChanging(false)}>{ct("settings.cancel_button", "Cancel")}</button>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-base text-amber-700 font-semibold mb-2">Change the household passcode? Any device that isn't already joined will need this new passcode to get in.</p>
            <div className="flex gap-2">
              <button className="flex-1 bg-slate-800 text-white text-xs font-bold rounded-lg py-1.5" onClick={confirmPasscodeChange}>{ct("settings.change_passcode_confirm", "Yes, change passcode")}</button>
              <button className="flex-1 bg-white border border-slate-200 text-xs font-bold rounded-lg py-1.5" onClick={() => setConfirmingChange(false)}>{ct("settings.cancel_button", "Cancel")}</button>
            </div>
          </div>
        )}
        </div>
      </CollapsibleSection>
      )}
      {/* Handover (Alan, 15 Sep): "the link to app process in Settings > Household". This is
          the install offer relocated to where an existing household finds it — for the person
          who continued in the browser at setup, whose ticket expired, or who deleted and
          reinstalled. InstallButton issues a fresh 24-hour ticket and carries it in the URL,
          so the installed app's first launch is minted without a passcode. Hidden when the
          app is already running standalone or the browser cannot install, which is the same
          rule the pitch-screen sheet follows. */}
      <SetUpAppCard code={code} open={openCard === "install"} onToggle={cardToggle("install")} />

      {ownerOnlyCards && (
      <CollapsibleSection variant="row" icon={<Icon id="ic-home" size={18} />} title={ct("settings.household.card_email", "Owner name and email")} open={openCard === "email"} onToggle={cardToggle("email")} summary={<p className="text-xs text-slate-500 truncate">{state.ownerEmail ? `${state.ownerEmail}${state.ownerEmailVerified ? " · " + ct("settings.household.sum_verified", "verified") : " · " + ct("settings.household.sum_unconfirmed", "unconfirmed")}` : ct("settings.household.sum_no_email", "Not set")}</p>}>
        <div className="pt-1">
        {/* LB-337 — the owner's first name, inside the owner card rather than beside it as a
            card of its own. One card for "who owns this household" is the version that needs
            no explaining (principle 2), and it is also what keeps the Household category's
            card list the shape check-settings-surfaces.mjs already pins. */}
        <div className="text-sm font-semibold text-slate-700 mb-1">{ct("settings.household.name_label", "Your first name")}</div>
        <p className="text-base text-slate-500 mb-2">{ct("settings.household.name_purpose", "How Little Bankers addresses you in emails and around the app.")}</p>
        {/* (6) No Add step. An empty name field with an "Add" button asks a parent to press a
            button to be allowed to type — so the input is live from the start with Save beside
            it, and once there IS a name the CTA reads Edit, because changing one is a
            different act from filling one in. */}
        {!editingName ? (
          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 text-sm font-semibold text-slate-800 bg-slate-50 rounded-lg py-2 px-3 border border-slate-100 truncate" data-testid="household-owner-name">
              {state.ownerName}
            </div>
            <button className="bg-slate-100 text-slate-600 font-bold text-xs rounded-lg px-3 py-2.5 flex-shrink-0" onClick={openChangeName} data-testid="household-owner-name-edit">
              {ct("settings.household.name_edit", "Edit")}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-4">
            <input
              type="text"
              className="flex-1 min-w-0 border border-slate-200 rounded-lg px-3 py-2 text-base"
              placeholder={ct("settings.household.name_label", "Your first name")}
              aria-label={ct("settings.household.name_label", "Your first name")}
              maxLength={OWNER_NAME_MAX}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              autoComplete="given-name"
              data-testid="household-owner-name-input"
            />
            <button className="bg-slate-800 text-white text-xs font-bold rounded-lg px-3 py-2.5 flex-shrink-0 disabled:opacity-40" disabled={!cleanOwnerName(newName)} onClick={saveOwnerName} data-testid="household-owner-name-save">{ct("settings.household.name_save", "Save")}</button>
          </div>
        )}
        {/* LB-338 made the Owner's name mandatory, which retired LB-337's "clearing it back
            to nothing is allowed". The two cannot both hold: OwnerNameSheet renders whenever
            the Owner has no name and cannot be dismissed, so a cleared name would seize the
            screen demanding it back — the app allowing a thing and then refusing to let you
            leave until you undo it.
            A greyed button on its own is the app refusing without saying why, which is
            principle 5 in reverse. It says why. */}
        {editingName && !cleanOwnerName(newName) && (
          <p className="text-xs text-slate-500 font-medium -mt-3 mb-4" data-testid="household-owner-name-required">
            {ct("settings.household.name_required", "Your name stays on the household, so everyone can see who approved what.")}
          </p>
        )}
        <div className="text-sm font-semibold text-slate-700 mb-1">{ct("settings.household.email_label", "Owner email")}</div>
        <p className="text-base text-slate-500 mb-2">{ct("settings.email_purpose", "Where the household code went at setup, and where passcode-reset links go if you ever need one.")}</p>
        {!changingEmail ? (
          <div className="flex items-center gap-2">
            <div className="flex-1 text-sm font-semibold text-slate-800 bg-slate-50 rounded-lg py-2 px-3 border border-slate-100 truncate">
              {state.ownerEmail || <span className="text-slate-400 font-normal">{ct("settings.not_set", "Not set")}</span>}
            </div>
            <button className="bg-slate-100 text-slate-600 font-bold text-xs rounded-lg px-3 py-2.5 flex-shrink-0" onClick={openChangeEmail}>
              {state.ownerEmail ? "Change" : "Add"}
            </button>
          </div>
        ) : !confirmingEmailChange ? (
          <div className="bg-slate-50 border border-slate-100 rounded-xl p-3">
            <input
              type="email"
              inputMode="email"
              className="border border-slate-200 rounded-lg px-3 py-2 text-base w-full mb-2"
              placeholder="Email address"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              autoComplete="email"
            />
            {emailError && <div className="text-rose-600 text-base font-semibold mb-2">{emailError}</div>}
            <div className="flex gap-2">
              <button className="flex-1 bg-slate-800 text-white text-xs font-bold rounded-lg py-1.5" onClick={submitEmailChange}>{ct("settings.save_email", "Save email")}</button>
              <button className="flex-1 bg-white border border-slate-200 text-xs font-bold rounded-lg py-1.5" onClick={() => setChangingEmail(false)}>{ct("settings.cancel_button", "Cancel")}</button>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-base text-amber-700 font-semibold mb-2">{ct("settings.household.emailchange_confirm", "Send a confirmation link to {email}? The owner email only changes when that link is clicked — and the current address will be told when it does.", { email: newEmail })}</p>
            <div className="flex gap-2">
              <button className="flex-1 bg-slate-800 text-white text-xs font-bold rounded-lg py-1.5" onClick={confirmEmailChange}>{ct("settings.household.emailchange_cta", "Send confirmation link")}</button>
              <button className="flex-1 bg-white border border-slate-200 text-xs font-bold rounded-lg py-1.5" onClick={() => setConfirmingEmailChange(false)}>{ct("settings.cancel_button", "Cancel")}</button>
            </div>
          </div>
        )}
        {emailChangePending && (
          <div className="mt-2 bg-teal-50 border border-teal-100 text-teal-800 text-xs font-semibold rounded-lg px-3 py-2">
            {ct("settings.household.emailchange_pending", "Confirmation sent to {email} — the owner email changes when the link in that message is clicked (it lasts 24 hours).", { email: emailChangePending })}
          </div>
        )}
        {state.ownerEmail && (
          state.ownerEmailVerified ? (
            <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-teal-700 bg-teal-50 rounded px-2 py-0.5">
              {ct("settings.household.email_verified", "Verified")}
            </div>
          ) : (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 rounded px-2 py-0.5">
                {ct("settings.household.email_unverified", "Unconfirmed. Check your inbox")}
              </div>
              <button className="text-xs font-bold text-teal-700 underline disabled:opacity-50" disabled={resendingVerification} onClick={resendVerification}>
                {resendingVerification ? "Sending…" : ct("settings.household.resend_verification", "Resend confirmation")}
              </button>
            </div>
          )
        )}
        </div>
      </CollapsibleSection>
      )}

      {/* Time zone (LB-316). The household's emails are sent at an hour that makes sense
          where the family lives; without this every household is treated as British. The
          card never shows a blank field or a list of four hundred IANA names to scroll
          (principles 1 and 2): the app already knows what this device says, so the whole
          choice is "keep what's stored" or "use this device's". A household that has
          never set one reads as Europe/London, which is stated rather than implied. */}
      <TimezoneCard state={state} update={update} showToast={showToast} open={openCard === "timezone"} onToggle={cardToggle("timezone")} />

      <CollapsibleSection variant="row" tone="danger" icon={<Icon id="ic-attention" hue="rose" size={18} />} title={ct("settings.household.card_leave", "Leave household on this device")} open={openCard === "leave"} onToggle={cardToggle("leave")}>
        <div className="pt-1">
        {!confirmingLeave ? (
          <>
            <p className="text-base text-slate-500 mb-2">Unlinks this device from household <b>{code}</b>. Data isn't deleted — you (or anyone with the code and passcode) can rejoin any time.</p>
            <button className="w-full bg-white border border-rose-300 text-rose-600 font-bold text-sm rounded-xl py-2.5" onClick={() => setConfirmingLeave(true)}>{ct("settings.leave_household", "Leave this household")}</button>
          </>
        ) : (
          <div className="bg-rose-50 border border-rose-200 rounded-xl p-3">
            <p className="text-base text-rose-700 font-semibold mb-1">Make sure you've saved this code before leaving:</p>
            <div className="text-center text-lg font-bold tracking-widest text-slate-800 bg-white rounded-lg py-2 mb-2 border border-rose-100">{code}</div>
            <p className="text-base text-rose-700 mb-2">This device will be unlinked and you'll be sent back to the start screen. Use the code above (and your passcode) to rejoin.</p>
            <div className="flex gap-2">
              <button className="flex-1 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold rounded-lg py-1.5" onClick={onLeaveHousehold}>{ct("settings.leave_household_confirm", "Yes, leave household")}</button>
              <button className="flex-1 bg-white border border-slate-200 text-xs font-bold rounded-lg py-1.5" onClick={() => setConfirmingLeave(false)}>{ct("settings.cancel_button", "Cancel")}</button>
            </div>
          </div>
        )}
        </div>
      </CollapsibleSection>
      </div>
    </div>
  );
}

/* -------------------------------- time zone ---------------------------------- */

// Settings → Household → Time zone (LB-316).
//
// The only thing this decides is WHEN the worker's lifecycle emails go out: a stage that
// sends at 17:30 means 17:xx where the household is. Nothing in the app reads it — habit
// days, week starts and the Monday interest run are all still London, deliberately, since
// moving those would change money and streaks rather than the hour an email lands.
//
// `settings.timezone` is absent for every household created before this shipped and for
// any device that would not report one. Absent means Europe/London in the worker, and the
// card says so in as many words rather than pre-filling a value the parent never chose.
function TimezoneCard({ state, update, showToast, open, onToggle }) {
  const stored = typeof state.settings.timezone === "string" ? state.settings.timezone : "";
  const device = browserTimezone();
  const shown = stored || "Europe/London";
  const canAdopt = !!device && device !== stored;

  function adopt() {
    if (!device) return;
    update((s) => { logSetting(s, getDeviceId(), "timezone", stored || "Europe/London", device, actor(s)); s.settings.timezone = device; return s; });
    showToast(ct("toast.timezone_set", "Time zone updated"), "good");
  }

  return (
    <CollapsibleSection
      variant="row"
      icon={<Icon id="ic-recent" size={18} />}
      title={ct("settings.household.card_timezone", "Time zone")}
      open={open}
      onToggle={onToggle}
      summary={<p className="text-xs text-slate-500 truncate">{stored ? shown : ct("settings.household.timezone_unset_sum", "Europe/London, the default")}</p>}
    >
      <div className="pt-1">
        <p className="text-base text-slate-500 mb-2">{ct("settings.household.timezone_purpose", "Emails we send about your household go out at a sensible hour where you are. Nothing else in the app uses this.")}</p>
        <div className="flex items-center gap-2">
          <div className="flex-1 text-sm font-semibold text-slate-800 bg-slate-50 rounded-lg py-2 px-3 border border-slate-100 truncate" data-testid="household-timezone-value">{shown}</div>
          {canAdopt && (
            <button className="bg-slate-100 text-slate-600 font-bold text-xs rounded-lg px-3 py-2.5 flex-shrink-0" data-testid="household-timezone-adopt" onClick={adopt}>
              {ct("settings.household.timezone_use_device", "Use {tz}", { tz: device })}
            </button>
          )}
        </div>
        {!canAdopt && device && (
          <p className="text-xs text-slate-400 font-medium mt-2">{ct("settings.household.timezone_matches", "This is the time zone this device is in.")}</p>
        )}
      </div>
    </CollapsibleSection>
  );
}

/* --------------------------- notification settings -------------------------- */

// Household-wide coverage, as opposed to the per-device toggle above it. Two different
// questions that look like one: "will this phone buzz?" and "will anyone's?". A parent
// reading only the toggle can be perfectly satisfied while the household has no coverage on
// any device that anyone actually carries.
// `refreshKey` (Aug 2026 bugfix): this card used to fetch once on mount and never again, so
// toggling the switch right above it — on or off — left this exact card showing a stale
// snapshot from before the change for the rest of the visit. A parent could turn
// notifications on, watch the switch flip green, and still be told "0 devices alerted"
// two inches below it. NotificationToggle bumps this counter on every successful
// enable/disable (see its onStatusChange call in SettingsTab below); bumping it here
// re-runs the fetch, same pattern as the `code` dependency already used for the first load.
function HouseholdCoverageCard({ code, state, refreshKey }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiGetPushStatus(code).then((s) => { if (!cancelled) setStatus(s); });
    return () => { cancelled = true; };
  }, [code, refreshKey]);

  if (!status) return null;

  const childrenCovered = (state.profiles || []).filter((p) => (status.children[p.id] || 0) > 0).length;
  const totalChildren = (state.profiles || []).length;

  // Native Run C, Part 2: the "Your household" group — the two counts as rows, the red line
  // when nobody is alerted inside the same card.
  return (
    <NotificationGroup label={ct("settings.notifications.group_household", "Your household")} testId="push-household-group">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <span className="text-sm font-semibold text-slate-700">{ct("settings.devices_alerted", "Devices alerted about requests")}</span>
        <span className={`text-xs font-bold rounded px-2.5 py-1 ${status.parents > 0 ? "text-emerald-600 bg-emerald-50" : "text-rose-600 bg-rose-50"}`}>
          {status.parents}
        </span>
      </div>
      {totalChildren > 0 && (
        <div className="flex items-center justify-between gap-3 px-4 py-3">
          <span className="text-sm font-semibold text-slate-700">Children who'll hear back</span>
          <span className={`text-xs font-bold rounded px-2.5 py-1 ${childrenCovered === totalChildren ? "text-emerald-600 bg-emerald-50" : "text-amber-600 bg-amber-50"}`}>
            {childrenCovered} of {totalChildren}
          </span>
        </div>
      )}
      {status.parents === 0 && (
        <p className="text-xs text-rose-500 font-semibold px-4 py-3">{ct("settings.nobody_alerted", "Nobody will be told when a child sends a request — turn the switch above on, on whichever device you actually carry.")}</p>
      )}
    </NotificationGroup>
  );
}

export { HouseholdManagementSection, HouseholdCoverageCard };
